import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getBudgetSettings, getBudgetStatus, saveBudgetSettings, updateBudgetUsage } from '../../services/budgetService';

const ADMIN_SESSION_KEY = 'vf_admin_session';

const checkAdminSession = () => {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(ADMIN_SESSION_KEY) === '1';
};

const ADMIN_ID = 'veeflix_admin';
const ADMIN_PASS = 'vfadmin008';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[AdminSettings] Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main style={{ minHeight: '100vh', background: '#111', color: '#fff', padding: '2rem' }}>
          <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', borderRadius: 8, padding: '1.5rem', maxWidth: 600, margin: '0 auto' }}>
            <h2 style={{ color: '#fca5a5', marginBottom: '1rem' }}>⚠️ Error Loading Settings</h2>
            <p style={{ color: '#fecaca', fontSize: '0.875rem', marginBottom: '1rem' }}>{this.state.error?.message || 'An unexpected error occurred'}</p>
            <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Reload Page</button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

export function AdminSettings() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [budgetSettings, setBudgetSettings] = useState({
    cloudinary: { enabled: true, limit: 10, alertAt: 0.8 },
    mux: { enabled: true, limit: 5, alertAt: 0.8 },
    vercel: { enabled: true, limit: 5, alertAt: 0.8 },
    general: { totalBudget: 20, dailyAutoRefresh: false },
  });

  const [currentUsage, setCurrentUsage] = useState({
    cloudinary: { storage: 0, bandwidth: 0 },
    mux: { minutes: 0, storage: 0 },
    vercel: { bandwidth: 0, builds: 0 },
  });

  const [saving, setSaving] = useState(false);

  const [changingPassword, setChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    try {
      const isAuth = checkAdminSession();
      if (isAuth) {
        setIsAuthenticated(true);
      } else {
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      console.error('[AdminSettings] Auth check failed:', err);
      setError(err.message);
      navigate('/admin', { replace: true });
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadSettings();
    }
  }, [isAuthenticated]);

  const loadSettings = async () => {
    try {
      const settings = await getBudgetSettings();
      const status = await getBudgetStatus();
      
      setBudgetSettings(settings);
      setCurrentUsage(status.usage || currentUsage);
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    }
    navigate('/admin', { replace: true });
  };

  const handleSaveBudget = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      await saveBudgetSettings(budgetSettings);
      await updateBudgetUsage(currentUsage);
      setSuccess('Budget settings saved successfully!');
    } catch (err) {
      console.error('Error saving budget settings:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = () => {
    setPasswordError('');
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    try {
      localStorage.setItem('vf_admin_new_pass', newPassword);
      setSuccess('Password updated! (Note: This is stored locally only. For production, use Firebase Auth)');
      setNewPassword('');
      setConfirmPassword('');
      setChangingPassword(false);
    } catch (err) {
      setPasswordError(err.message);
    }
  };

  if (loading) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'grid', placeItems: 'center' }}>
        <p>Loading...</p>
      </main>
    );
  }

  if (!isAuthenticated) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'grid', placeItems: 'center' }}>
        <p style={{ color: '#666' }}>Redirecting to login...</p>
      </main>
    );
  }

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', path: '/admin' },
    { id: 'media', label: 'Media Upload', path: '/admin/media' },
    { id: 'invites', label: 'Invites', path: '/admin/invites' },
    { id: 'users', label: 'Users', path: '/admin/users' },
    { id: 'albums', label: 'Albums', path: '/admin/albums' },
    { id: 'settings', label: 'Settings', path: '/admin/settings' },
  ];

  return (
    <ErrorBoundary>
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff' }}>
        {error && (
          <div style={{ background: '#7f1d1d', padding: '1rem', borderBottom: '1px solid #ef4444', textAlign: 'center' }}>
            <p style={{ color: '#fecaca', margin: 0 }}>⚠️ {error}</p>
          </div>
        )}
        
        {success && (
          <div style={{ background: '#14532d', padding: '1rem', borderBottom: '1px solid #16a34a', textAlign: 'center' }}>
            <p style={{ color: '#86efac', margin: 0 }}>✓ {success}</p>
          </div>
        )}
        
        <header style={{ background: '#1a1a1a', borderBottom: '1px solid #333', padding: '0 1.5rem' }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Link to="/admin" style={{ textDecoration: 'none' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#e50914' }}>VEEFLIX</span>
              </Link>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Admin</span>
            </div>
            <button onClick={handleLogout} style={{ padding: '0.4rem 0.8rem', borderRadius: 6, border: '1px solid #444', background: 'transparent', color: '#9ca3af', fontSize: '0.8rem', cursor: 'pointer' }}>Logout</button>
          </div>
        </header>

        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              <Link
                key={tab.id}
                to={tab.path}
                style={{
                  padding: '0.5rem 0.9rem',
                  borderRadius: 6,
                  textDecoration: 'none',
                  color: tab.id === 'settings' ? '#fff' : '#9ca3af',
                  background: tab.id === 'settings' ? '#333' : 'transparent',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {tab.label}
              </Link>
            ))}
          </div>

          <h1 style={{ marginBottom: '1.5rem' }}>Settings</h1>

          <div style={{ display: 'grid', gap: '1.5rem', maxWidth: 800 }}>
            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.5rem' }}>
              <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Budget Settings</h2>
              <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                Set spending limits for Cloudinary, Mux, and Vercel. You'll be alerted when usage exceeds 80% of the limit.
              </p>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af' }}>Total Monthly Budget ($)</label>
                <input
                  type="number"
                  value={budgetSettings.general.totalBudget}
                  onChange={(e) => setBudgetSettings({ ...budgetSettings, general: { ...budgetSettings.general, totalBudget: parseFloat(e.target.value) || 0 } })}
                  style={{ width: '100%', maxWidth: 200, padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#9ca3af', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={budgetSettings.general.dailyAutoRefresh}
                    onChange={(e) => setBudgetSettings({ ...budgetSettings, general: { ...budgetSettings.general, dailyAutoRefresh: e.target.checked } })}
                  />
                  Auto-refresh budget data daily
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ padding: '1rem', background: '#222', borderRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600 }}>Cloudinary</span>
                    <input type="checkbox" checked={budgetSettings.cloudinary.enabled} onChange={(e) => setBudgetSettings({ ...budgetSettings, cloudinary: { ...budgetSettings.cloudinary, enabled: e.target.checked } })} />
                  </div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Limit ($)</label>
                  <input type="number" value={budgetSettings.cloudinary.limit} onChange={(e) => setBudgetSettings({ ...budgetSettings, cloudinary: { ...budgetSettings.cloudinary, limit: parseFloat(e.target.value) || 0 } })} disabled={!budgetSettings.cloudinary.enabled} style={{ width: '100%', padding: '0.4rem', borderRadius: 4, border: '1px solid #333', background: '#111', color: '#fff' }} />
                </div>

                <div style={{ padding: '1rem', background: '#222', borderRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600 }}>Mux</span>
                    <input type="checkbox" checked={budgetSettings.mux.enabled} onChange={(e) => setBudgetSettings({ ...budgetSettings, mux: { ...budgetSettings.mux, enabled: e.target.checked } })} />
                  </div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Limit ($)</label>
                  <input type="number" value={budgetSettings.mux.limit} onChange={(e) => setBudgetSettings({ ...budgetSettings, mux: { ...budgetSettings.mux, limit: parseFloat(e.target.value) || 0 } })} disabled={!budgetSettings.mux.enabled} style={{ width: '100%', padding: '0.4rem', borderRadius: 4, border: '1px solid #333', background: '#111', color: '#fff' }} />
                </div>

                <div style={{ padding: '1rem', background: '#222', borderRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600 }}>Vercel</span>
                    <input type="checkbox" checked={budgetSettings.vercel.enabled} onChange={(e) => setBudgetSettings({ ...budgetSettings, vercel: { ...budgetSettings.vercel, enabled: e.target.checked } })} />
                  </div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '0.25rem' }}>Limit ($)</label>
                  <input type="number" value={budgetSettings.vercel.limit} onChange={(e) => setBudgetSettings({ ...budgetSettings, vercel: { ...budgetSettings.vercel, limit: parseFloat(e.target.value) || 0 } })} disabled={!budgetSettings.vercel.enabled} style={{ width: '100%', padding: '0.4rem', borderRadius: 4, border: '1px solid #333', background: '#111', color: '#fff' }} />
                </div>
              </div>

              <button onClick={handleSaveBudget} disabled={saving} style={{ padding: '0.75rem 1.5rem', background: saving ? '#444' : '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save Budget Settings'}
              </button>
            </div>

            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.5rem' }}>
              <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Admin Credentials</h2>
              <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                Current credentials: <code style={{ background: '#222', padding: '0.2rem 0.4rem', borderRadius: 4 }}>veeflix_admin</code> / <code style={{ background: '#222', padding: '0.2rem 0.4rem', borderRadius: 4 }}>vfadmin008</code>
              </p>

              {!changingPassword ? (
                <button onClick={() => setChangingPassword(true)} style={{ padding: '0.6rem 1.2rem', background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                  Change Password
                </button>
              ) : (
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af' }}>New Password</label>
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={{ width: '100%', maxWidth: 300, padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af' }}>Confirm Password</label>
                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={{ width: '100%', maxWidth: 300, padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }} />
                  </div>
                  {passwordError && <p style={{ color: '#ef4444', marginBottom: '1rem' }}>{passwordError}</p>}
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button onClick={() => setChangingPassword(false)} style={{ padding: '0.6rem 1.2rem', background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                    <button onClick={handleChangePassword} style={{ padding: '0.6rem 1.2rem', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Update Password</button>
                  </div>
                </div>
              )}
            </div>

            <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1.5rem' }}>
              <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Quick Links</h2>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <a href="https://console.cloudinary.com" target="_blank" rel="noopener noreferrer" style={{ padding: '0.6rem 1rem', background: '#3448c5', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.875rem' }}>
                  Cloudinary Dashboard →
                </a>
                <a href="https://dashboard.mux.com" target="_blank" rel="noopener noreferrer" style={{ padding: '0.6rem 1rem', background: '#5436d4', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.875rem' }}>
                  Mux Dashboard →
                </a>
                <a href="https://vercel.com" target="_blank" rel="noopener noreferrer" style={{ padding: '0.6rem 1rem', background: '#000', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: '0.875rem' }}>
                  Vercel Dashboard →
                </a>
                <a href="https://console.firebase.google.com" target="_blank" rel="noopener noreferrer" style={{ padding: '0.6rem 1rem', background: '#ffa726', color: '#000', borderRadius: 6, textDecoration: 'none', fontSize: '0.875rem' }}>
                  Firebase Console →
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
    </ErrorBoundary>
  );
}

export default AdminSettings;